import { useState, useCallback } from 'react';
import type { SyncState } from '@/types/common';
import styles from '@/styles/components/SyncIndicator.module.css';

interface SyncIndicatorProps {
  syncState: SyncState;
}

export function SyncIndicator({ syncState }: SyncIndicatorProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const toggleTooltip = useCallback(() => {
    setShowTooltip((prev) => !prev);
  }, []);

  const { isOnline, isSyncing, lastSyncedAt, lastSyncError } = syncState;

  let icon: string;
  let stateClass: string;
  let label: string;

  if (isSyncing) {
    icon = '↻';
    stateClass = styles.syncing;
    label = 'Syncing';
  } else if (!isOnline) {
    icon = '☁';
    stateClass = styles.offline;
    label = 'Offline';
  } else if (lastSyncError) {
    icon = '!';
    stateClass = styles.error;
    label = 'Sync error';
  } else {
    icon = '✓';
    stateClass = styles.synced;
    label = 'Synced';
  }

  const tooltipText = lastSyncedAt
    ? `Last synced: ${new Date(lastSyncedAt).toLocaleString()}`
    : lastSyncError
      ? `Error: ${lastSyncError}`
      : 'Not yet synced';

  return (
    <div className={styles.container}>
      <button
        className={`${styles.indicator} ${stateClass}`}
        onClick={toggleTooltip}
        aria-label={label}
        title={label}
      >
        <span className={isSyncing ? styles.spinner : undefined}>{icon}</span>
      </button>
      {showTooltip && <div className={styles.tooltip}>{tooltipText}</div>}
    </div>
  );
}
